import { useEffect, useState } from 'react'
import { resolveBookIdentity } from '../lib/bookLogoRegistry.js'
import './LiveOddsVNextMatrix.css'

function formatOdds(value, format) {
  if (value == null || value === '') return '—'
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  if (format === 'decimal') return (number >= 100 ? 1 + number / 100 : 1 + 100 / Math.abs(number)).toFixed(2)
  const rounded = Math.round(number)
  return rounded > 0 ? `+${rounded}` : String(rounded)
}

function formatLine(value, selection) {
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  if (/^over$/i.test(selection)) return `O ${number}`
  if (/^under$/i.test(selection)) return `U ${number}`
  return number > 0 ? `+${number}` : String(number)
}

function BookLogo({ book }) {
  const [failed, setFailed] = useState(false)
  const identity = resolveBookIdentity(book)
  return identity.logoUrl && !failed
    ? <img src={identity.logoUrl} alt={identity.displayName} title={identity.displayName} onError={() => setFailed(true)} />
    : <span title={identity.displayName}>{identity.displayName.slice(0, 3)}</span>
}

function Cell({ cell, selection, oddsFormat, onInspect }) {
  if (!cell) return <td className="lovxm-cell lovxm-empty">—</td>
  const ev = Number.isFinite(cell.evPercent) ? cell.evPercent : null
  return (
    <td className={`lovxm-cell ${cell.best ? 'best' : ''}`}>
      <button type="button" onClick={() => onInspect(cell.row)} title="Inspect source row, history, provenance, and betslip link">
        {cell.row.line != null && <small>{formatLine(cell.row.line, selection)}</small>}
        <strong>{formatOdds(cell.row.american, oddsFormat)}</strong>
        {ev !== null && ev > 0 && <em>+{ev.toFixed(1)}%</em>}
      </button>
    </td>
  )
}

function MarketLabel({ market, selection }) {
  return (
    <div className="lovxm-label">
      <strong>{selection}</strong>
      <span>{market.period} · {market.betType}{market.line == null ? '' : ` · ${market.line}`}</span>
      {market.integrityStatus !== 'ok' && <em>raw line-sign conflict · fair/EV disabled</em>}
    </div>
  )
}

function DesktopMatrix({ items, books, oddsFormat, onInspect }) {
  let priorEvent = null
  return (
    <div className="lovxm-scroll">
      <table className="lovxm-table">
        <thead><tr><th className="lovxm-label-col">Market / selection</th><th className="lovxm-fair-col">Fair</th>
          {books.map((book) => <th key={book} className="lovxm-book"><BookLogo book={book} /></th>)}
        </tr></thead>
        <tbody>{items.flatMap(({ event, market }) => {
          const rows = []
          if (event.key !== priorEvent) {
            priorEvent = event.key
            rows.push(<tr className="lovxm-event" key={`${event.key}:event`}><td colSpan={books.length + 2}>
              <b>{event.league}</b><strong>{event.event}</strong><time>{event.etDate || 'date unknown'} · {event.etTime || 'time unknown'}</time>
            </td></tr>)
          }
          market.selections.forEach((selection) => {
            const fair = market.fair.bySelection[selection]
            rows.push(<tr key={`${event.key}|${market.key}|${selection}`}>
              <td className="lovxm-sticky"><MarketLabel market={market} selection={selection} /></td>
              <td className={`lovxm-fair ${market.integrityStatus !== 'ok' ? 'conflict' : ''}`} title={market.integrityStatus !== 'ok'
                ? `Integrity gate: ${market.integrityStatus} (${market.conflictSelections.join(', ')})`
                : `${market.fair.method}; ${market.fair.weighting}; ${fair?.bookCount || 0} complete book markets`}>
                <strong>{formatOdds(fair?.american, oddsFormat)}</strong><small>{fair?.bookCount || 0} books</small>
              </td>
              {books.map((book) => <Cell key={book} cell={market.cells[selection]?.[book]} selection={selection} oddsFormat={oddsFormat} onInspect={onInspect} />)}
            </tr>)
          })
          return rows
        })}</tbody>
      </table>
    </div>
  )
}

function MobileMatrix({ items, oddsFormat, onInspect }) {
  return <div className="lovxm-mobile">{items.map(({ event, market }) => (
    <article key={`${event.key}|${market.key}`}>
      <header><b>{event.league}</b><strong>{event.event}</strong><time>{event.etDate} · {event.etTime}</time></header>
      {market.selections.map((selection) => {
        const cells = Object.values(market.cells[selection] || {}).sort((left, right) => Number(right.row.american) - Number(left.row.american))
        const fair = market.fair.bySelection[selection]
        return <section key={selection}>
          <div><MarketLabel market={market} selection={selection} /><span className="lovxm-mobile-fair">Fair {formatOdds(fair?.american, oddsFormat)} · {fair?.bookCount || 0} books</span></div>
          <div className="lovxm-mobile-books">{cells.map((cell) => <button className={cell.best ? 'best' : ''} type="button" key={cell.row.book} onClick={() => onInspect(cell.row)}>
            <BookLogo book={cell.row.book} /><strong>{formatOdds(cell.row.american, oddsFormat)}</strong>{cell.row.line != null && <small>{formatLine(cell.row.line, selection)}</small>}
          </button>)}</div>
        </section>
      })}
    </article>
  ))}</div>
}

export default function LiveOddsVNextMatrix({ items, oddsFormat, onInspect }) {
  const books = [...new Set(items.flatMap(({ market }) => market.books))]
  const [mobile, setMobile] = useState(() => globalThis.matchMedia?.('(max-width: 700px)').matches ?? false)
  useEffect(() => {
    const query = globalThis.matchMedia?.('(max-width: 700px)')
    if (!query) return undefined
    const update = () => setMobile(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return <section className="lovxm-shell" aria-label="Married sportsbook odds matrix">
    {mobile
      ? <MobileMatrix items={items} oddsFormat={oddsFormat} onInspect={onInspect} />
      : <DesktopMatrix items={items} books={books} oddsFormat={oddsFormat} onInspect={onInspect} />}
  </section>
}
